import React, { useState } from "react"
import {
	Done as PluginEnabledIcon,
	HighlightOff as PluginDisabledIcon
} from "@material-ui/icons"
import {
	Grid,
	Typography,
	Button,
	CircularProgress,
	Chip,
	FormControl,
	FormLabel,
	TextField
} from "@material-ui/core"

import { Notification, Portlet, PopConfirm } from "@/components"
import PluginGroupPermissionGate from "@/components/PermissionGate/PluginGroupPermissionGate"

import RDStationCRMService, { RDStationCRMEncryptStateDataInput } from "@/services/RDStationCRM"
import ErrorHandlerService from "@/services/ErrorHandler"

import { rdStationCRMLinks } from "@/utils/link"

import useValidation, { ErrorType } from "@/hooks/useValidation"

import { RDStationCRMPluginSettingsData } from "@/protocols/RdStationPluginProtocol"

import useStyles from "@/pages/Admin/Plugins/RDStationPlugin/RDStationCRMPlugin/styles"
import { colors } from "@/styles/colors"

type SettingsProps = {
	plugin: RDStationCRMPluginSettingsData
	onUpdate: (data: Partial<RDStationCRMPluginSettingsData>) => void
}

const Settings: React.FC<SettingsProps> = (props) => {
	const {
		plugin,
		onUpdate
	} = props

	const [loading, setLoading] = useState(false)

	const classes = useStyles()

	const { validation, triggerValidation, clearValidation } = useValidation()

	const pluginIsConnected = plugin?.status === "enabled"

	const [credentials, setCredentials] = useState<RDStationCRMEncryptStateDataInput["data"]>({
		clientId: plugin?.settings?.client_id ?? "",
		clientSecret: plugin?.settings?.client_secret ?? ""
	})

	const handleChangeCredentials = (field: keyof RDStationCRMEncryptStateDataInput["data"], value: string) => {
		clearValidation(field)

		setCredentials({
			...credentials,
			[field]: value
		})
	}

	const handleDisconnect = async () => {
		PopConfirm.open({
			title: "Desconectar plugin do RD Station CRM",
			description: "Deseja realmente desconectar este plugin? Você pode conectá-lo novamente quando quiser, mas algumas configurações podem ser perdidas.",
			onConfirm: async () => {
				try {
					await RDStationCRMService.revoke(plugin.id as number)

					onUpdate({
						...plugin,
						status: "disabled",
						settings: {
							...plugin.settings,
							client_id: "",
							client_secret: ""
						}
					})

					Notification.success({ message: "Plugin desconectado com sucesso." })
				} catch (error) {
					ErrorHandlerService.handle(error as Error)
					Notification.error({ message: "Não foi possível desconectar o plugin." })
				}
			},
			confirmButtonText: "DESCONECTAR"
		})
	}

	const handleConnect = async () => {
		setLoading(true)
		try {
			const rdStationCredentials = await RDStationCRMService.encryptStateData({
				pluginId: Number(plugin.id),
				data: credentials
			})

			const rdStationCRMAuthUrl = rdStationCRMLinks.getRdStationCRMAuthUrl(
				rdStationCredentials.clientId,
				rdStationCRMLinks.rdStationCRMCallback,
				rdStationCredentials.token
			)

			onUpdate({
				...plugin,
				settings: {
					...plugin.settings,
					client_id: credentials.clientId,
					client_secret: credentials.clientSecret
				}
			})

			window.open(`${rdStationCRMAuthUrl}`, "_self")
		} catch (error) {
			triggerValidation(error as ErrorType)
			Notification.error({ message: "Não foi possível conectar o plugin." })
		}
		setLoading(false)
	}

	return (
		<Portlet>
			<Grid
				container
				direction="column"
				spacing={8}
			>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						direction="column"
						spacing={4}
					>
						<Grid item xs={12}>
							<Grid
								container
								justifyContent="space-between"
							>
								<Grid item>
									<Typography variant="h4">
										Acesso da API no RD Station CRM
									</Typography>
									<Typography variant="body1">
										Conecte a sua conta do RD Station CRM à Letalk.
									</Typography>
								</Grid>

								<Grid item>
									<Chip
										size="small"
										onDelete={() => true}
										style={{
											pointerEvents: "none"
										}}
										{...(pluginIsConnected ? {
											label: "Plugin Conectado",
											className: classes.authPluginStatus,
											deleteIcon: <PluginEnabledIcon style={{ color: colors.gray[0] }} />
										} : {
											label: "Plugin Desconectado",
											className: classes.unAuthPluginStatus,
											deleteIcon: <PluginDisabledIcon />
										})}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Grid container direction="column" spacing={2}>
								<Grid item xs={8}>
									<FormControl fullWidth focused={false} style={{ gap: 8 }}>
										<FormLabel> <b>Client ID</b> </FormLabel>
										<TextField
											variant="outlined"
											fullWidth
											placeholder="Client ID do RD Station CRM"
											disabled={loading}
											onChange={(event) => handleChangeCredentials("clientId", event.target.value)}
											value={credentials.clientId}
											helperText={validation.client_id}
											error={!!validation.client_id}
										/>
									</FormControl>
								</Grid>

								<Grid item xs={8}>
									<FormControl fullWidth focused={false} style={{ gap: 8 }}>
										<FormLabel> <b>Client Secret</b> </FormLabel>
										<TextField
											variant="outlined"
											fullWidth
											placeholder="Client Secret do RD Station CRM"
											disabled={loading}
											onChange={(event) => handleChangeCredentials("clientSecret", event.target.value)}
											value={credentials.clientSecret}
											helperText={validation.client_secret}
											error={!!validation.client_secret}
										/>
									</FormControl>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Grid
						container
						justifyContent="flex-end"
					>
						<Grid item>
							<PluginGroupPermissionGate action={pluginIsConnected ? "disconnect" : "connect"}>
								<Button
									{...(pluginIsConnected ? {
										className: classes.authPluginButton
									} : {
										color: "primary"
									})}
									variant="contained"
									disabled={loading}
									endIcon={loading && <CircularProgress size={20} />}
									onClick={async () => {
										if (pluginIsConnected) {
											await handleDisconnect()
											return
										}
										await handleConnect()
									}}
								>
									{pluginIsConnected ? "Desinstalar" : "Instalar Plugin"}
								</Button>
							</PluginGroupPermissionGate>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default Settings
